/**
 * Norwegian bokmål UI text. Every user-visible string in the web app lives here; look strings up
 * with `t("section.key", { param })` from `./t`. Placeholders use `{name}` syntax.
 */
export const nb = {
  app: {
    name: "Skoleveianalyse",
    skipToContent: "Hopp til innhold",
    loading: "Laster …",
    tagline: "Skoleveianalyse for kommuner",
  },
  nav: {
    main: "Hovedmeny",
    overview: "Oversikt",
    tiltak: "Tiltaksliste",
    changes: "Endringer",
    fau: "FAU",
    exports: "Eksport",
    members: "Medlemmer",
    admin: "Admin",
    menu: "Meny",
    switcher: "Kommune",
    switcherPlaceholder: "Velg kommune",
    switcherOption: "{kommune} ({org})",
    signedInAs: "Innlogget som {name}",
    logout: "Logg ut",
    loggingOut: "Logger ut …",
  },
  footer: {
    label: "Bunntekst",
    privacy: "Personvern",
    dpa: "Databehandling",
    attributionDefault:
      "Inneholder data under norsk lisens for offentlige data (NLOD) gjort tilgjengelig av Statens vegvesen og Utdanningsdirektoratet; © Kartverket (CC BY 4.0)",
  },
  common: {
    save: "Lagre",
    saving: "Lagrer …",
    saved: "Lagret",
    cancel: "Avbryt",
    retry: "Prøv igjen",
    close: "Lukk",
    none: "Ingen",
    unknown: "Ukjent",
    dash: "–",
    previous: "Forrige",
    next: "Neste",
    pagination: "Sidenavigasjon",
    pageOf: "Side {page} av {pages}",
    showing: "Viser {from}–{to} av {total}",
    search: "Søk",
    remove: "Fjern",
    confirmRemove: "Bekreft fjerning",
    kmh: "{value} km/t",
    meters: "{value} m",
    yes: "Ja",
    no: "Nei",
    working: "Arbeider …",
  },
  errors: {
    generic: "Noe gikk galt. Prøv igjen.",
    network: "Fikk ikke kontakt med tjenesten. Sjekk nettforbindelsen og prøv igjen.",
    forbidden: "Du har ikke tilgang til dette.",
    noAccessKommune: "Du har ikke tilgang til denne kommunen.",
    notFoundTitle: "Fant ikke siden",
    notFoundBody: "Siden du leter etter finnes ikke, eller du har ikke tilgang til den.",
    toHome: "Til forsiden",
    meFailed: "Kunne ikke hente innloggingsstatus.",
  },
  severity: {
    high: "Høy",
    medium: "Middels",
    low: "Lav",
  },
  status: {
    ny: "Ny",
    planlagt: "Planlagt",
    utfort: "Utført",
    ikke_relevant: "Ikke relevant",
  },
  levels: {
    barnetrinn: "1.–7. trinn",
    ungdomstrinn: "8.–10. trinn",
  },
  roles: {
    owner: "Eier",
    member: "Medlem",
  },
  auth: {
    loginTitle: "Logg inn",
    loginIntro: "Logg inn med e-post og passord. Nye brukere får tilgang via invitasjon.",
    email: "E-post",
    password: "Passord",
    submitLogin: "Logg inn",
    loggingIn: "Logger inn …",
    invalidCredentials: "Feil e-post eller passord.",
    rateLimited: "For mange mislykkede forsøk. Vent 15 minutter og prøv igjen.",
    forgotLink: "Glemt passord?",
    forgotTitle: "Glemt passord",
    forgotIntro:
      "Skriv inn e-postadressen din, så sender vi en lenke der du kan velge nytt passord.",
    forgotSubmit: "Send lenke",
    forgotSent:
      "Hvis e-postadressen er registrert hos oss, har vi sendt en lenke for å velge nytt passord. Lenken er gyldig en begrenset tid. Sjekk også søppelpost.",
    backToLogin: "Tilbake til innlogging",
    resetTitle: "Velg nytt passord",
    newPassword: "Nytt passord",
    confirmPassword: "Gjenta passordet",
    passwordHint: "Minst 10 tegn.",
    passwordTooShort: "Passordet må ha minst 10 tegn.",
    passwordMismatch: "Passordene er ikke like.",
    resetSubmit: "Lagre nytt passord",
    resetDone: "Passordet er endret. Du kan nå logge inn med det nye passordet.",
    toLogin: "Gå til innlogging",
    inviteTitle: "Godta invitasjon",
    inviteIntro: "Du er invitert til {org} med rollen {role}.",
    inviteEmail: "Invitasjonen gjelder {email}.",
    inviteExpires: "Invitasjonen er gyldig til {date}.",
    inviteExisting:
      "Du har allerede en konto. Godta invitasjonen for å bli lagt til i organisasjonen, og logg deretter inn med ditt eksisterende passord.",
    inviteAccept: "Godta invitasjon",
    inviteNew: "Velg navn og passord for å opprette kontoen din.",
    name: "Navn",
    createAccount: "Opprett konto",
    inviteInvalid: "Invitasjonen er ugyldig eller utløpt. Be den som inviterte deg om en ny.",
    inviteAcceptedExisting: "Du er lagt til i organisasjonen.",
  },
  home: {
    title: "Velg kommune",
    intro: "Du har tilgang til flere kommuner. Velg hvilken du vil se.",
    orgKommuner: "Kommuner i {org}",
    noKommuner: "Ingen kommuner er knyttet til organisasjonen ennå.",
    noOrgs: "Du er ikke medlem av noen organisasjon ennå. Kontakt den som inviterte deg.",
    adminLink: "Gå til plattformadministrasjon",
    membersLink: "Medlemmer i {org}",
  },
  dashboard: {
    kommuneNummer: "Kommune {nummer}",
    runInfo: "Data hentet {snapshot}. Analysen ble fullført {finished}.",
    criteria: "Kriteriesett: {title} ({version})",
    disclaimerTitle: "Om bruk av analysen",
    disclaimer:
      "Analysen er et faglig hjelpemiddel for å prioritere trafikksikkerhetstiltak. Den er ikke et vedtak eller en vurdering av om en skolevei er særlig farlig eller vanskelig etter opplæringslova; det avgjøres av kommunen i hvert enkelt tilfelle, ut fra blant annet barnets alder og modenhet. Analysen bygger på registrerte data i åpne kilder (NVDB, NSR, Matrikkelen). Eksponering er antall adresser, ikke elever, og hver adresse er tildelt nærmeste offentlige skole. Ruten er modellert, ikke observert: korteste vei og en plausibel tryggere rute. Analysen ser ikke forhold som ikke er registrert, som foreldrekjøring, parkering, sikt, vinterdrift, skolekrets, skolevalg eller faktisk valgt rute.",
    noRunTitle: "Ingen analyse ennå",
    noRunBody:
      "Det er ikke kjørt noen analyse for {navn} ennå. Plattformadministrator starter analysen, og resultatene vises her når den er ferdig.",
    warningsTitle: "Merk: begrenset datagrunnlag",
    syntheticTitle: "Syntetiske testdata",
    syntheticBody:
      "Denne analysen bygger på syntetiske testdata og viser ikke reelle forhold i kommunen.",
    totalsTitle: "Funn etter alvorlighet",
    totalFindings: "{count} funn totalt",
    statusTitle: "Oppfølging",
    schoolsTitle: "Skoler",
    schoolsCaption: "Grunnskoler i {navn} med antall funn",
    colSchool: "Skole",
    colTrinn: "Trinn",
    colAddresses: "Adresser",
    colHigh: "Høy",
    colMedium: "Middels",
    colLow: "Lav",
    colOpen: "Åpne funn",
    colLinks: "Vis",
    mapLink: "Kart",
    tiltakLink: "Tiltak",
    mapLinkLabel: "Kart for {school}",
    tiltakLinkLabel: "Tiltaksliste for {school}",
    trinn: "{from}.–{to}.",
    within2km: "{count} innen 2 km",
    withHigh: "Adresser med alvorlig funn: {withHigh} av {addresses} ({pct} %)",
    notSnapped: "Skolen kunne ikke knyttes til vegnettet.",
    latestChange: "Det er registrert endringer i analysen.",
    latestChangeLink: "Se hva som er endret",
    noSchools: "Analysen fant ingen grunnskoler.",
    openTiltak: "Åpne tiltakslisten",
  },
  map: {
    back: "Tilbake til oversikten",
    tiltakLink: "Tiltaksliste for skolen",
    trinn: "{trinn}. trinn",
    layers: "Kartlag",
    layerCatchment: "Skolekrets",
    layerRoutes: "Skoleveier",
    layerFindings: "Funn",
    layerCrossings: "Kryssinger",
    layerAccidents: "Trafikkulykker",
    layerFau: "Meldinger fra foreldre",
    legend: "Tegnforklaring",
    legendRoutes: "Skolevei (tykkere linje = flere adresser)",
    legendDimmed: "Utført eller ikke relevant (nedtonet)",
    legendCrossingWith: "Kryssing med tilrettelegging",
    legendCrossingWithout: "Kryssing uten tilrettelegging",
    legendSchool: "Skole",
    legendAccident: "Ulykke",
    legendCluster: "Ulykkesklynge",
    legendFau: "Godkjent melding fra foreldre",
    accidentsNote: "Ulykker vises som bakgrunnsinformasjon og påvirker ikke vurderingen.",
    mapLabel: "Kart over skoleveier til {school}",
    loadingMap: "Laster kart …",
    mapUnavailable: "Kartet kunne ikke vises i denne nettleseren.",
    findingsList: "Funn ved skolen ({count})",
    noFindings: "Ingen funn for denne skolen.",
    selectHint: "Velg et funn i kartet eller i listen for å se detaljer og sette status.",
  },
  finding: {
    details: "Detaljer om funnet",
    close: "Lukk detaljer",
    rank: "Rangering",
    rankValue: "Nr. {rank}",
    severity: "Alvorlighet",
    location: "Sted",
    explanation: "Hvorfor er dette flagget?",
    tiltak: "Mulige tiltak",
    exposure: "Eksponering",
    exposureValue: "{count} adresser har skolevei her",
    exposureRange: "Eksponering (lav–høy)",
    exposureRangeValue: "{low}–{high} adresser kan ha skolevei her",
    confidence: "Vurderingssikkerhet",
    confidenceRobust: "Robust",
    confidenceScenario: "Scenarioavhengig",
    confidenceShortest: "Kun korteste rute",
    confidencePlausible: "Kun tryggere rute",
    speed: "Fartsgrense",
    aadt: "ÅDT",
    aadtValue: "{value} kjøretøy per døgn",
    aadtYear: "{value} kjøretøy per døgn ({year})",
    length: "Lengde",
    accidents: "Ulykker i nærheten",
    accidentsValue: "{count}, hvorav {soft} med myke trafikanter",
    fau: "FAU-bekreftelser",
    fauValue: "Bekreftet av {count} foreldre",
    schools: "Skoler",
    criterion: "Kriterium",
    vegref: "Vegreferanser",
    nvdbRefs: "NVDB-referanser",
    nvdbSeq: "Veglenkesekvens {id}, posisjon {fra}–{til}",
    nvdbPos: "Veglenkesekvens {id}, posisjon {pos}",
    nvdbObjects: "NVDB-objekter",
    objectType: "{type}: {ids}",
    notInRun: "Funnet finnes ikke i gjeldende analyse.",
    followUp: "Oppfølging",
    surroundings: "Omgivelser",
    surroundingsBoliger: "Boliger i nærheten",
    surroundingsBoligerText:
      "{count} adresser ligger innen 100 m. Et fysisk tiltak som fortau eller gangfelt kommer dem til gode.",
    surroundingsFaaBoliger: "Få boliger i nærheten",
    surroundingsFaaBoligerText:
      "Bare {count} adresser ligger innen 100 m. Fortau eller gang- og sykkelveg koster mye per bolig her. Vurder fartsreduksjon, eller om elevene som bruker strekningen har rett til skyss fordi skoleveien er særlig farlig.",
    surroundingsFaaBoligerPointText:
      "Bare {count} adresser ligger innen 100 m. Vurder fartsreduksjon, eller om elevene som bruker skoleveien her har rett til skyss fordi skoleveien er særlig farlig.",
  },
  statusEditor: {
    status: "Status",
    statusFor: "Status for {title}",
    comment: "Kommentar",
    commentFor: "Kommentar for {title}",
    commentPlaceholder: "Valgfri kommentar",
    save: "Lagre",
    saveComment: "Lagre kommentar",
    saving: "Lagrer …",
    saved: "Lagret",
    savedStatus: "Status for «{title}» er lagret som {status}.",
    lastUpdated: "Sist endret {date}",
  },
  tiltak: {
    title: "Tiltaksliste",
    intro:
      "Funn rangert etter prioritet. Prioriteten tar hensyn til alvorlighet og hvor mange adresser som har skolevei forbi stedet.",
    filters: "Filtrer tiltakslisten",
    school: "Skole",
    allSchools: "Alle skoler",
    severity: "Alvorlighet",
    status: "Status",
    criterion: "Kriterium",
    allCriteria: "Alle kriterier",
    surroundings: "Omgivelse",
    allSurroundings: "Alle",
    surroundingsBoliger: "Boliger",
    surroundingsFaaBoliger: "Få boliger",
    search: "Søk i sted eller tittel",
    searchButton: "Søk",
    sort: "Sortering",
    sortRank: "Rangering",
    sortExposure: "Eksponering",
    sortSeverity: "Alvorlighet",
    sortStatus: "Status",
    reset: "Nullstill filtre",
    caption: "Tiltaksliste, {total} funn",
    colRank: "Rang",
    colSeverity: "Alvorlighet",
    colTitle: "Funn",
    colLocation: "Sted",
    colSchools: "Skole",
    colExposure: "Eksponering",
    colAadt: "ÅDT",
    colSpeed: "Fart",
    colFau: "FAU",
    colStatus: "Status og kommentar",
    empty: "Ingen funn passer filtrene.",
    noRun: "Kommunen har ingen ferdig analyse ennå.",
    exposureTitle: "Antall adresser med skolevei her",
    fauTitle: "Antall bekreftelser fra foreldre",
  },
  changes: {
    title: "Endringer",
    intro:
      "Når data i NVDB endres, kjøres analysen på nytt og sammenlignes med forrige analyse. Her ser du hva som er nytt, løst eller endret.",
    empty: "Ingen endringer er registrert ennå.",
    caption: "Registrerte endringer",
    colDate: "Dato",
    colNew: "Nye",
    colResolved: "Løste",
    colChanged: "Endret alvorlighet",
    colLink: "Detaljer",
    details: "Se detaljer",
    detailsLabel: "Se endringer fra {date}",
    detailTitle: "Endringer {date}",
    summary: "{new} nye, {resolved} løste og {changed} med endret alvorlighet.",
    newTitle: "Nye funn",
    resolvedTitle: "Løste funn",
    resolvedNote: "Disse funnene finnes ikke lenger i den nye analysen.",
    changedTitle: "Endret alvorlighet",
    none: "Ingen.",
    fromTo: "{from} → {to}",
    rank: "Nr. {rank}",
    showInList: "Vis i tiltaksliste",
    showOnMap: "Vis i kart",
    backToList: "Alle endringer",
  },
  fau: {
    title: "FAU-innspill",
    intro:
      "Opprett en kampanje og del lenken eller QR-koden med FAU. Foreldre kan bekrefte steder fra analysen eller melde inn nye. Innspillene vises først etter at dere har godkjent dem.",
    campaignsTitle: "Kampanjer",
    campaignsEmpty: "Ingen kampanjer ennå.",
    newCampaign: "Ny kampanje",
    createTitle: "Ny kampanje",
    editTitle: "Endre kampanje",
    fieldTitle: "Tittel",
    fieldTitleHint: "Vises for foreldrene, for eksempel «Trygg skolevei høsten 2026».",
    fieldSchool: "Skole",
    allSchools: "Alle skoler",
    schoolLocked: "Skolen kan ikke endres etter at kampanjen er opprettet.",
    fieldOpens: "Åpner",
    fieldCloses: "Stenger",
    fieldDailyCap: "Maks antall innspill per døgn (valgfritt)",
    fieldDailyCapHint: "Tomt felt gir standardgrensen. Beskytter mot misbruk.",
    fieldDailyCapHintEdit: "Tomt felt beholder gjeldende grense. Beskytter mot misbruk.",
    titleRequired: "Skriv inn en tittel.",
    alreadyDecided: "Innspillet var allerede behandlet av en annen. Listen er oppdatert.",
    create: "Opprett kampanje",
    save: "Lagre endringer",
    created: "Kampanjen «{title}» er opprettet.",
    updated: "Kampanjen «{title}» er oppdatert.",
    revoked: "Kampanjen «{title}» er trukket tilbake. Lenken virker ikke lenger.",
    closesBeforeOpens: "Stenger må være etter Åpner.",
    edit: "Endre",
    editLabel: "Endre kampanjen {title}",
    revoke: "Trekk tilbake",
    revokeLabel: "Trekk tilbake kampanjen {title}",
    revokeConfirmTitle: "Trekke tilbake kampanjen?",
    revokeConfirmBody:
      "Lenken og QR-koden for «{title}» slutter å virke med en gang. Innspill som allerede er sendt inn, beholdes. Dette kan ikke angres.",
    revokeConfirm: "Trekk tilbake",
    period: "{opens} – {closes}",
    periodLabel: "Periode",
    statusPlanlagt: "Planlagt",
    statusApen: "Åpen",
    statusStengt: "Stengt",
    statusTrukket: "Trukket tilbake",
    counts: "{pending} til behandling, {approved} godkjent, {rejected} avvist",
    dailyCap: "Maks {cap} innspill per døgn",
    link: "Lenke for foreldre",
    linkFor: "Lenke for {title}",
    copy: "Kopier lenke",
    copyLabel: "Kopier lenken til {title}",
    copied: "Lenken er kopiert.",
    copyFailed: "Kunne ikke kopiere. Marker lenken og kopier den manuelt.",
    openLink: "Åpne",
    qr: "QR-kode",
    qrAlt: "QR-kode med lenken til kampanjen {title}",
    qrDownload: "Last ned QR-kode",
    qrDownloadLabel: "Last ned QR-kode for {title}",
    moderationTitle: "Innspill",
    moderationIntro:
      "Godkjente innspill vises i kartet, i tiltakslisten og i rapporter. Når et innspill avvises, slettes kommentaren.",
    tabs: "Filtrer innspill etter status",
    tabPending: "Til behandling",
    tabApproved: "Godkjent",
    tabRejected: "Avvist",
    campaignFilter: "Kampanje",
    allCampaigns: "Alle kampanjer",
    reportsEmpty: "Ingen innspill her.",
    kindConfirm: "Bekreftelse av funn",
    kindNew: "Nytt farlig sted",
    category: "Kategori",
    comment: "Kommentar",
    noComment: "Ingen kommentar",
    commentDeleted: "Kommentaren er slettet",
    finding: "Gjelder",
    point: "Punkt: {lat}, {lon}",
    received: "Mottatt {date}",
    approve: "Godkjenn",
    reject: "Avvis",
    approveLabel: "Godkjenn innspill mottatt {date}",
    rejectLabel: "Avvis innspill mottatt {date}",
    rejectHint: "Avvis sletter kommentaren.",
    approved: "Innspillet er godkjent.",
    rejected: "Innspillet er avvist og kommentaren er slettet.",
    reportsCaption: "Innspill",
    campaign: "Kampanje",
    showOnMap: "Vis i kart",
  },
  fauPublic: {
    loading: "Henter kartet …",
    notFoundTitle: "Lenken finnes ikke",
    notFoundBody: "Sjekk at du har skrevet av hele lenken, eller spør FAU om en ny.",
    revokedTitle: "Lenken er ikke lenger i bruk",
    closedTitle: "Innspill er ikke åpent nå",
    kommune: "{kommune} kommune",
    intro:
      "Hjelp kommunen å finne steder på skoleveien som oppleves som farlige. Du kan bekrefte et sted som allerede er markert, eller melde inn et nytt.",
    openUntil: "Du kan sende innspill til {date}.",
    schools: "Gjelder: {schools}",
    privacyShort:
      "Ikke skriv navn eller andre personopplysninger. Vi lagrer ikke navn, e-postadresse eller IP-adresse.",
    privacyLink: "Les mer om personvern",
    mapLabel: "Kart over skoleveien med markerte steder",
    mapUnavailable: "Kartet kunne ikke vises. Du kan fortsatt bekrefte steder fra listen under.",
    findingsTitle: "Steder analysen har markert",
    findingsEmpty: "Ingen steder er markert for denne skolen.",
    confirm: "Bekreft dette stedet",
    confirmLabel: "Bekreft {title}, {location}",
    newPoint: "Meld nytt farlig sted",
    placeHint:
      "Trykk på kartet der det farlige stedet er. Du kan trykke på nytt for å flytte punktet.",
    useCenter: "Bruk midten av kartet",
    pointPlaced: "Punktet er plassert.",
    pointMissing: "Trykk på kartet for å markere stedet først.",
    confirmTitle: "Bekreft sted",
    newTitle: "Nytt farlig sted",
    category: "Hva er problemet?",
    categoryMissing: "Velg hva problemet er.",
    comment: "Kommentar (valgfri)",
    commentTooLong: "Kommentaren kan ha maksimalt 500 tegn.",
    commentWarning: "Ikke skriv navn eller andre personopplysninger.",
    counter: "{count} av {max} tegn",
    submit: "Send inn",
    sending: "Sender …",
    cancel: "Avbryt",
    thanksTitle: "Takk for innspillet!",
    again: "Send inn et nytt innspill",
    honeypot: "Ikke fyll ut dette feltet",
    severity: "Alvorlighet: {severity}",
    selected: "Valgt: {title}, {location}",
  },
  exports: {
    title: "Eksport",
    intro:
      "Last ned en PDF-rapport til trafikksikkerhetsplanen eller en tiltaksliste i Excel. Nye analyser gir også arket Skyssgrunnlag i Excel-filen, med adressene som har et funn med høy alvorlighet på skoleveien. Filene lages i bakgrunnen og er klare etter kort tid.",
    kommuneTitle: "Hele kommunen",
    pdf: "Last ned PDF-rapport",
    xlsx: "Last ned tiltaksliste (Excel)",
    schoolsTitle: "Per skole",
    schoolsCaption: "Eksport per skole",
    colSchool: "Skole",
    colActions: "Eksport",
    pdfShort: "PDF",
    xlsxShort: "Excel",
    pdfFor: "PDF-rapport for {school}",
    xlsxFor: "Tiltaksliste i Excel for {school}",
    ordered: "{kind} er bestilt. Den vises i listen under når den er klar.",
    ready: "{kind} for {scope} er klar til nedlasting.",
    listTitle: "Bestilte filer",
    listCaption: "Bestilte filer",
    listEmpty: "Ingen filer er bestilt ennå.",
    colCreated: "Bestilt",
    colKind: "Type",
    colKommune: "Kommune",
    colScope: "Omfang",
    colStatus: "Status",
    colFile: "Fil",
    kindPdf: "PDF-rapport",
    kindXlsx: "Tiltaksliste (Excel)",
    scopeKommune: "Hele kommunen",
    statusQueued: "I kø",
    statusRunning: "Lages …",
    statusSucceeded: "Ferdig",
    statusFailed: "Feilet",
    download: "Last ned",
    downloadLabel: "Last ned {filename}",
    goToExports: "Se bestilte filer",
  },
  members: {
    title: "Medlemmer",
    intro: "Medlemmer i {org}.",
    caption: "Medlemmer",
    colName: "Navn",
    colEmail: "E-post",
    colRole: "Rolle",
    colActions: "Handlinger",
    you: "du",
    roleFor: "Rolle for {name}",
    removeLabel: "Fjern {name}",
    confirmRemoveLabel: "Bekreft fjerning av {name}",
    removed: "{name} er fjernet fra organisasjonen.",
    roleChanged: "Rollen til {name} er endret til {role}.",
    onlyOwners: "Bare eiere kan invitere nye medlemmer og endre roller.",
    inviteTitle: "Inviter kollega",
    inviteEmail: "E-post",
    inviteRole: "Rolle",
    inviteSubmit: "Send invitasjon",
    inviteSent: "Invitasjon sendt til {email}.",
    pendingTitle: "Ventende invitasjoner",
    pendingCaption: "Ventende invitasjoner",
    pendingEmpty: "Ingen ventende invitasjoner.",
    colSent: "Sendt",
    colExpires: "Utløper",
    revoke: "Trekk tilbake",
    revokeLabel: "Trekk tilbake invitasjonen til {email}",
    revoked: "Invitasjonen til {email} er trukket tilbake.",
  },
  contact: {
    title: "Bestill gjennomgang",
    lead: "Fortell oss litt om kommunen, så går vi gjennom analysen av deres skoler i et kort videomøte. De første kommunene får første år gratis som pilot.",
    kommuneLine: "Gjelder kommunenummer {nr}",
    name: "Navn",
    email: "E-post",
    organisation: "Kommune eller organisasjon",
    role: "Rolle",
    rolePlaceholder: "Velg rolle (valgfritt)",
    roleCoordinator: "Trafikksikkerhetskoordinator",
    roleSchool: "Skole eller oppvekst",
    roleTechnical: "Teknisk/samferdsel",
    roleFau: "FAU",
    roleConsultant: "Rådgiver eller konsulent",
    roleOther: "Annet",
    message: "Melding (valgfritt)",
    messageHint: "For eksempel hvilke skoler dere er mest opptatt av.",
    honeypot: "La dette feltet stå tomt",
    privacy:
      "Vi bruker opplysningene bare til å svare deg, og sletter dem automatisk etter 24 måneder.",
    privacyLink: "Les mer i personvernerklæringen.",
    submit: "Send",
    sending: "Sender …",
    successTitle: "Takk for henvendelsen!",
    successBody: "Vi svarer normalt innen to virkedager.",
    error: "Noe gikk galt. Prøv igjen om litt.",
  },
  admin: {
    title: "Plattformadministrasjon",
    noAccess: "Denne siden krever plattformadministrator.",
    tabs: "Administrasjon",
    tabOrgs: "Organisasjoner",
    tabKommuner: "Kommuner",
    tabRuns: "Analysekjøringer",
    tabJobs: "Jobber",
    tabLeads: "Henvendelser",
    leadsCaption: "Henvendelser fra kontaktskjemaet",
    leadsEmpty: "Ingen henvendelser ennå.",
    colReceived: "Mottatt",
    colEmail: "E-post",
    colOrganisation: "Organisasjon",
    colRole: "Rolle",
    colMessage: "Melding",
    leadOpen: "Ny",
    leadHandled: "Behandlet {date}",
    markHandled: "Marker som behandlet",
    markHandledLabel: "Marker henvendelsen fra {name} som behandlet",
    leadMarked: "Henvendelsen fra {name} er markert som behandlet.",
    refresh: "Oppdater",
    orgCreateTitle: "Opprett organisasjon",
    orgName: "Navn",
    orgKind: "Type",
    kindKommune: "Kommune",
    kindRadgiver: "Rådgiverfirma",
    kindAnnet: "Annet",
    orgCreate: "Opprett",
    orgCreated: "Organisasjonen {name} er opprettet.",
    orgsEmpty: "Ingen organisasjoner ennå.",
    orgMembers: "{count} medlemmer",
    orgCreatedAt: "Opprettet {date}",
    orgKommuner: "Kommuner",
    orgNoKommuner: "Ingen kommuner tildelt.",
    unassign: "Fjern",
    unassignLabel: "Fjern {kommune} fra {org}",
    unassigned: "{kommune} er fjernet fra {org}.",
    assignTitle: "Tildel kommune",
    kommuneNummer: "Kommunenummer",
    kommuneNummerHint: "Fire siffer, for eksempel 3301.",
    kommuneNavn: "Kommunenavn (valgfritt)",
    assign: "Tildel",
    assigned: "{nummer} er tildelt {org}.",
    nummerInvalid: "Kommunenummeret må ha fire siffer.",
    inviteTitle: "Inviter bruker",
    inviteEmail: "E-post",
    inviteRole: "Rolle",
    inviteSubmit: "Send invitasjon",
    kommuneCreateTitle: "Legg til kommune",
    kommuneCreate: "Legg til",
    kommuneCreated: "Kommune {nummer} er lagt til.",
    kommunerCaption: "Kommuner",
    kommunerEmpty: "Ingen kommuner ennå.",
    colNummer: "Nummer",
    colNavn: "Navn",
    colOrgs: "Organisasjoner",
    colLatestRun: "Siste analyse",
    colActions: "Handlinger",
    noRun: "Ingen analyse",
    runSummary: "{findings} funn ({high} høy, {medium} middels, {low} lav)",
    synthetic: "syntetiske data",
    triggerRun: "Start analyse",
    triggerRunLabel: "Start analyse for {kommune}",
    runQueued: "Analyse for {nummer} er lagt i kø (jobb {job}).",
    demoPdf: "Generer demo-PDF",
    demoPdfLabel: "Generer demo-PDF for {kommune}",
    demoQueued: "Demo-PDF for {nummer} er bestilt.",
    demoTitle: "Demo-eksporter",
    demoIntro:
      "Demo-PDF-er inneholder bare analysedata (ingen status, kommentarer eller FAU-innspill) og kan vises til kommuner som ikke er kunder ennå.",
    demoEmpty: "Ingen demo-eksporter ennå.",
    demoCaption: "Demo-eksporter",
    runsCaption: "Analysekjøringer",
    runsEmpty: "Ingen analysekjøringer.",
    runsFilter: "Kommunenummer",
    runsFilterAll: "Alle kommuner",
    colStarted: "Startet",
    colKommune: "Kommune",
    colStatus: "Status",
    colReason: "Årsak",
    colVersion: "Versjon",
    colFinished: "Ferdig",
    colResult: "Resultat / feil",
    versionValue: "Motor {engine}, kriterier {criteria}",
    jobsCaption: "Jobber",
    jobsEmpty: "Ingen jobber.",
    jobsFilter: "Status",
    jobsFilterAll: "Alle",
    colId: "Id",
    colKind: "Type",
    colAttempts: "Forsøk",
    colRunAt: "Planlagt",
    colLastError: "Siste feil",
    attemptsValue: "{attempts} av {max}",
    retry: "Prøv på nytt",
    retryLabel: "Prøv jobb {id} på nytt",
    retried: "Jobb {id} er lagt i kø på nytt.",
    runStatusQueued: "I kø",
    runStatusRunning: "Kjører",
    runStatusSucceeded: "Fullført",
    runStatusFailed: "Feilet",
  },
  marketing: {
    nav: "Informasjon",
    seeKommune: "Se din kommune",
    how: "Slik fungerer det",
    prices: "Priser",
    login: "Logg inn",
    toApp: "Gå til appen",
    orderReview: "Bestill gjennomgang",
    nationalMap: "Åpne nasjonalt demokart",
  },
  kommuneSearch: {
    label: "Finn din kommune",
    placeholder: "Skriv kommunenavn eller nummer",
    results: "Treff",
    noMatch: "Ingen kommuner passer med «{query}».",
    loadFailed: "Kunne ikke hente kommunelisten.",
  },
  publicStatus: {
    red: "Rød",
    yellow: "Gul",
    green: "Grønn",
    grey: "Uten nok data",
  },
  landing: {
    title: "Skoleveianalyse for kommuner og FAU",
    heroEyebrow: "For kommuner og FAU",
    heroTitle: "Se hvor skoleveiene er farligst – for alle skolene i kommunen",
    heroLead:
      "Skoleveianalyse beregner gangveien fra hver bolig til skolen, finner strekninger og kryssinger med høy fart og uten fortau, og rangerer dem etter hvor mange boliger som har skoleveien sin der. Resultatet er en tiltaksliste dere kan bruke i trafikksikkerhetsplanen og i søknader om trafikksikkerhetsmidler.",
    statLine: "{schools} skoler i {kommuner} kommuner er allerede analysert.",
    whyTitle: "Hvorfor",
    whyOne:
      "En manuell skoleveikartlegging tar uker, dekker ofte bare noen få skoler og blir utdatert når vegnettet endres.",
    whyTwo:
      "Skoleveianalyse gjør det samme for alle grunnskolene på én gang, ut fra åpne offentlige data, og holder analysen oppdatert.",
    featuresTitle: "Dette får kommunen",
    howTitle: "Slik fungerer det",
    honestTitle: "Ærlig om metoden",
    pricesTitle: "Priser",
    pricesLead:
      "Årsabonnement per kommune, eks. mva. Alt er inkludert: kart, tiltaksliste, rapporter, FAU-innspill, endringsvarsel og ubegrenset antall brukere.",
    pilot:
      "Pilot: De første kommunene får første år gratis mot å gi oss tilbakemelding og være referanse.",
    advisers:
      "Over 40 skoler: pris etter avtale. Rådgivere og konsulentfirma: ta kontakt for lisens som dekker flere kommuner.",
    heroFigAlt: "Kart over skoleveiene til Berg skole i Larvik med funn merket etter alvorlighet",
    heroFigCaption:
      "Fra eksempelrapporten for Larvik: skoleveiene til Berg skole og funnene langs dem.",
    reportLink: "Last ned eksempelrapporten (PDF)",
    reportOr: "eller",
    excelLink: "tiltakslisten med skyssgrunnlag (Excel)",
    reportSee: "Se hvordan rapporten ser ut:",
    faqTitle: "Spørsmål og svar",
    ctaTitle: "Se analysen for deres skoler",
  },
  kommunerPage: {
    title: "Skoleveier i din kommune",
    lead: "Velg kommunen for å se en foreløpig vurdering av skoleveiene ved hver grunnskole.",
    listTitle: "Alle kommuner",
    schoolsMany: "{n} skoler",
    schoolsOne: "1 skole",
    redCount: "{n} røde",
    yellowCount: "{n} gule",
    loadFailed: "Kunne ikke hente kommunelisten.",
  },
  kommunePublic: {
    title: "Skoleveier i {kommune}",
    titleGeneric: "Skoleveier i kommunen",
    meta: "Kommunenummer {nr}",
    metaFetched: "Kommunenummer {nr} · data hentet {date}",
    notFound: "Fant ikke kommunenummer {nr}.",
    backToList: "Tilbake til alle kommuner",
    loadFailed: "Kunne ikke hente resultatene.",
    summaryMany:
      "{schools} grunnskoler: {red} røde, {yellow} gule, {green} grønne, {grey} uten nok data",
    summaryOne: "1 grunnskole: {red} røde, {yellow} gule, {green} grønne, {grey} uten nok data",
    noSchools: "Ingen grunnskoler er analysert for denne kommunen ennå.",
    summaryLabel: "Fordeling av skolene",
    chip: "{label}: {n}",
    disclaimer:
      "Dette er en foreløpig, automatisk vurdering. Rød betyr at et alvorlig funn berører mange av skolens adresser. Det er ikke et vedtak om farlig skolevei. Kriteriesettet er et utkast som ennå ikke er faglig gjennomgått.",
    howColour: "Slik settes fargen",
    noticeLabel: "Om vurderingen",
    schoolsTitle: "Skoler",
    trinn: "{trinn}. trinn",
    topFindings: "Viktigste funn",
    noFindings: "Ingen funn over terskel i registrerte data.",
    exposure: "Berører {exposure} av {addresses} adresser",
    tiltak: "Mulig tiltak: {tiltak}",
    kommuneExposure:
      "Til sammen har {withHigh} av {addresses} adresser innenfor gangavstand minst ett alvorlig funn på skoleveien.",
    schoolExposure:
      "{withHigh} av {addresses} adresser har minst ett alvorlig funn på skoleveien ({pct} %)",
    faaBoliger: "Få boliger i nærheten",
    faaBoligerHint: "Vurder fartsreduksjon eller skyss.",
    dataTitle: "Datagrunnlag",
    ctaTitle: "Vil dere ha hele analysen for {kommune}?",
    ctaOne: "Alle funn, ikke bare de viktigste",
    ctaTwo: "Kart per skole med skoleveier og ulykker",
    ctaThree: "Tiltaksliste med status, PDF og Excel",
    ctaFour: "FAU-innspill og varsel ved vegendringer",
    ctaFive: "Antall adresser med alvorlig funn per skole og per strekning",
    ctaOrder: "Bestill gjennomgang",
    ctaMap: "Se i nasjonalt kart",
  },
} as const;

/** Structured copy for the landing page (lists and cards; flat strings are in `nb.landing`). */
export interface TitledText {
  title: string;
  text: string;
}

export interface PriceTier {
  name: string;
  schools: string;
  price: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export const landingContent: {
  features: readonly TitledText[];
  steps: readonly TitledText[];
  honest: readonly string[];
  prices: readonly PriceTier[];
  faq: readonly FaqItem[];
} = {
  features: [
    {
      title: "Kart per skole",
      text: "Sannsynlige skoleveier fra alle boligadresser, med funn, kryssinger og trafikkulykker i samme kart.",
    },
    {
      title: "Rangert tiltaksliste",
      text: "Hvert funn forklart med fartsgrense, trafikkmengde, antall berørte adresser og kilde i NVDB. Status og kommentarer følger med til neste analyse.",
    },
    {
      title: "PDF-rapport og Excel",
      text: "Klar til vedlegg i trafikksikkerhetsplanen og i søknad om fylkeskommunale trafikksikkerhetsmidler. Excel-filen lister også adressene der skoleveien går forbi et alvorlig funn, som grunnlag når dere vurderer skyss på grunn av særlig farlig skolevei.",
    },
    {
      title: "Innspill fra FAU",
      text: "Del en lenke eller QR-kode. Foreldre bekrefter funn eller melder nye farepunkter fra mobilen, uten innlogging. Kommunen godkjenner før noe vises.",
    },
    {
      title: "Varsel ved endringer",
      text: "Analysen kjøres på nytt når vegdata endres, og dere får e-post om nye, løste og endrede funn.",
    },
    {
      title: "For hele kommunen",
      text: "Ubegrenset antall brukere, roller for eier og medlem, og egne tilganger for rådgivere som jobber for flere kommuner.",
    },
  ],
  steps: [
    {
      title: "Vi henter åpne data",
      text: "Vegnett, fartsgrenser, trafikkmengde (ÅDT), fortau og gangfelt fra Nasjonal vegdatabank, skoler fra Nasjonalt skoleregister, adresser fra Kartverket og trafikkulykker fra Statens vegvesen.",
    },
    {
      title: "Vi beregner skoleveiene",
      text: "Hver adresse kobles til nærmeste skole på riktig trinn langs vegnettet, innenfor 4 km. Hver strekning og kryssing vurderes mot et åpent, versjonert kriteriesett.",
    },
    {
      title: "Dere får resultatet",
      text: "Kart, tiltaksliste og rapporter med innlogging for kommunens ansatte. Analysen oppdateres når vegdata endres.",
    },
  ],
  honest: [
    "Analysen bygger på registrerte data. Mangler NVDB fortau eller trafikkmengde for en veg, står det i rapporten for hver kommune.",
    "Kriteriesettet er et utkast som ennå ikke er faglig gjennomgått. Alle terskler er åpne, og hvert funn viser hvilket kriterium som slo ut.",
    "Analysen erstatter ikke befaring. Den viser hvor dere bør se først.",
    "Analysen bruker adressepunkter, ikke opplysninger om hvem som bor der. Data lagres på server i EU.",
  ],
  prices: [
    { name: "Liten", schools: "1–5 skoler", price: "15 000 kr / år" },
    { name: "Mellomstor", schools: "6–15 skoler", price: "35 000 kr / år" },
    { name: "Stor", schools: "16–40 skoler", price: "60 000 kr / år" },
  ],
  faq: [
    {
      question: "Må vi installere noe?",
      answer: "Nei. Alt kjører i nettleseren, og vi setter opp kommunen for dere.",
    },
    {
      question: "Hvor raskt kommer vi i gang?",
      answer: "Vanligvis innen en uke. Analysen av en kommune tar minutter, ikke uker.",
    },
    {
      question: "Hva er forskjellen på demokartet og abonnementet?",
      answer:
        "Demokartet viser en foreløpig farge per skole og de viktigste funnene. Abonnementet gir hele tiltakslisten, kart per skole, rapporter, FAU-innspill og varsler.",
    },
    {
      question: "Kan vi bruke rapporten i søknader?",
      answer:
        "Ja. PDF-rapporten og Excel-lista er laget for å legges ved trafikksikkerhetsplanen og søknader om trafikksikkerhetsmidler.",
    },
    {
      question: "Hvem står bak?",
      answer:
        "Skoleveianalyse er et norsk selskap under oppstart. Ta kontakt, så viser vi analysen for deres kommune.",
    },
  ],
};

/**
 * Long-form content for the static information pages. Kept here (not in components) so all
 * user-facing Norwegian text lives in this file.
 */
export interface InfoSection {
  heading: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
}

export interface InfoPage {
  title: string;
  lead: string;
  updated: string;
  sections: readonly InfoSection[];
}

export const privacyPage: InfoPage = {
  title: "Personvern ved innmelding fra foreldre",
  lead: "Denne siden forklarer hvordan opplysninger behandles når du bekrefter eller melder inn farlige steder på skoleveien via en lenke fra kommunen eller FAU.",
  updated: "Sist oppdatert 13. oktober 2026.",
  sections: [
    {
      heading: "Hvem er ansvarlig?",
      paragraphs: [
        "Kommunen som har sendt deg lenken, er behandlingsansvarlig for opplysningene. Skoleveianalyse er databehandler og behandler opplysningene bare på vegne av kommunen og etter kommunens instrukser, regulert i en databehandleravtale.",
      ],
    },
    {
      heading: "Hva lagres når du sender inn?",
      bullets: [
        "Hvilket funn du bekrefter, eller hvor i kartet du har markert et nytt farlig sted.",
        "Kategorien du velger.",
        "En valgfri kommentar på inntil 500 tegn.",
        "Tidspunktet for innmeldingen.",
      ],
      paragraphs: [
        "Vi ber ikke om og lagrer ikke navn, e-postadresse, telefonnummer eller bilder. Ikke skriv navn eller andre opplysninger om deg selv eller andre i kommentaren.",
      ],
    },
    {
      heading: "IP-adresse og vern mot misbruk",
      paragraphs: [
        "For å hindre misbruk begrenser vi hvor mange meldinger som kan sendes inn. Til dette bruker vi bare en enveis hash av IP-adressen, laget med en tilfeldig verdi som byttes hver dag. IP-adressen din lagres aldri i klartekst, verken i databasen eller i logger.",
      ],
    },
    {
      heading: "Moderering før publisering",
      paragraphs: [
        "Alle meldinger blir gjennomgått av kommunen før noe blir synlig. Bare godkjente meldinger vises i kartet, i tiltakslisten og i rapporter.",
      ],
    },
    {
      heading: "Hvor lenge lagres opplysningene?",
      paragraphs: [
        "Fritekst (kommentarer) slettes automatisk 24 måneder etter at innmeldingsperioden er avsluttet. Alle data lagres på servere i EU/EØS.",
      ],
    },
    {
      heading: "Dine rettigheter",
      paragraphs: [
        "Fordi vi ikke vet hvem du er, kan en melding normalt ikke knyttes til deg. Har du spørsmål, eller vil du be om innsyn, retting eller sletting, kontakter du kommunen som har sendt deg lenken. Du kan også klage til Datatilsynet.",
      ],
    },
    {
      heading: "Henvendelser via kontaktskjemaet",
      paragraphs: [
        "Når du sender en henvendelse via kontaktskjemaet «Bestill gjennomgang», lagrer vi navn, e-postadresse, organisasjon, rolle, kommune og meldingen du skriver.",
        "Formålet er å svare på henvendelsen og avtale en demonstrasjon. Det rettslige grunnlaget er berettiget interesse (personvernforordningen artikkel 6 nr. 1 bokstav f).",
        "Opplysningene slettes automatisk 24 måneder etter at vi mottok dem. Vil du ha dem slettet før, eller se hva vi har lagret, svarer du på e-posten fra oss eller sender en ny henvendelse via kontaktskjemaet.",
        "IP-adressen din lagres ikke. For å stoppe spam bruker vi bare en enveis hash av IP-adressen, laget med en tilfeldig verdi som byttes hver dag, til en daglig teller.",
      ],
    },
  ],
};

export const dpaPage: InfoPage = {
  title: "Databehandling for kommuner",
  lead: "Informasjon til kommuner om hvilke data Skoleveianalyse behandler, hvor de lagres og hvor lenge. Kommunen er behandlingsansvarlig for personopplysninger i tjenesten, og vi er databehandler.",
  updated: "Sist oppdatert 13. oktober 2026.",
  sections: [
    {
      heading: "Hvilke data behandles?",
      bullets: [
        "Brukerkontoer: navn, e-postadresse, rolle og passord (lagret som en sikker hash, aldri i klartekst).",
        "Innloggingsøkter og en revisjonslogg over endringer, for eksempel status på funn, moderering og medlemskap.",
        "Kommunens oppfølging: status og kommentarer på funn, FAU-kampanjer og eksporterte rapporter.",
        "Meldinger fra foreldre (FAU): kategori, plassering, valgfri kommentar på inntil 500 tegn og tidspunkt. Ingen navn, e-postadresser eller bilder.",
        "IP-adresser lagres ikke. For å begrense misbruk av FAU-siden brukes bare en hash av IP-adressen med en verdi som byttes daglig.",
        "Åpne data: vegnett og vegobjekter fra NVDB (Statens vegvesen), skoler fra Nasjonalt skoleregister (Utdanningsdirektoratet) og adresser og kart fra Kartverket. Dette er ikke personopplysninger.",
      ],
    },
    {
      heading: "Hvor lagres data?",
      paragraphs: [
        "Alle data lagres og behandles i EU/EØS, på servere hos Hetzner Online GmbH. Sikkerhetskopier lagres også hos Hetzner i EU/EØS.",
      ],
    },
    {
      heading: "Underleverandører",
      bullets: [
        "Hetzner Online GmbH: drift av servere og lagring av sikkerhetskopier (EU/EØS).",
        "Brevo: utsending av e-post, som invitasjoner, lenker for nytt passord og varsel om endringer (EU/EØS).",
      ],
    },
    {
      heading: "Lagringstid",
      bullets: [
        "Fritekst i FAU-meldinger slettes 24 måneder etter at kampanjen er avsluttet.",
        "Brukerkontoer og kommunens oppfølgingsdata beholdes så lenge avtalen løper, og slettes når avtalen avsluttes.",
        "Innloggingsøkter utløper automatisk.",
      ],
    },
    {
      heading: "Databehandleravtale",
      paragraphs: [
        "Vi bruker kommunens standard databehandleravtale. Avtalen inngås før tjenesten tas i bruk med reelle brukere eller FAU-kampanjer.",
      ],
    },
  ],
};
